import { ChangeEvent, ComponentType, HTMLProps, memo, useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { v1 } from "uuid";
import { Tooltip } from "../../../ui";

import { ReactComponent as IndeterminateIcon } from "./icons/indeterminate.svg";
import { ReactComponent as SelectedIcon } from "./icons/selected.svg";

import styles from "./style.module.scss";

export type CheckboxProps = Omit<HTMLProps<HTMLInputElement>, "value" | "onChange"> & {
  indeterminate?: boolean,
  value?: boolean,
  checked?: boolean,
  onChange?: (e: ChangeEvent<HTMLInputElement>, value: boolean) => void,
  label?: string,
  hasError?: boolean,
  errorText?: string,
}

const Checkbox = memo<CheckboxProps>((
  {
    id,
    indeterminate,
    value,
    checked,
    disabled,
    onChange,
    label,
    className,
    title,
    // todo implement errors
    hasError,
    errorText,
    ...restProps
  }) => {
  const resolvedRef = useRef<any>();
  const realId = useMemo(() => id || v1(), [ id ]);
  const onValueChanged = useMemo(() => (e: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      return onChange?.(e, e.target.checked);
    }
  }, [ disabled, onChange ]);

  useEffect(() => {
    if (resolvedRef.current) {
      resolvedRef.current.indeterminate = indeterminate;
    }
  }, [ resolvedRef, indeterminate ]);

  const isChecked = (value === undefined ? (checked) : value) || false;


  const element = (
    <label
      htmlFor={realId}
      className={classNames(styles.root, className, { [styles.error]: hasError })}
      data-type="checkbox"
      data-state={indeterminate ? "indeterminate" : isChecked}
      data-input-disabled={disabled}
      data-input-error={!!hasError}
    >
      <input
        {...restProps}
        id={realId}
        ref={resolvedRef}
        value={label}
        checked={isChecked}
        disabled={disabled}
        onChange={onValueChanged}
        type="checkbox"
      />
      <div title={title} role="button" className={styles.icon}>
        {
          indeterminate
            ? <IndeterminateIcon />
            : (
              isChecked
                ? <SelectedIcon />
                : null
            )
        }
      </div>
      <span title={title} className={styles.label}>
        {label}
      </span>
    </label>
  );

  return hasError
    ? (
      <Tooltip content={errorText} options={{ placement: "bottom-start" }}>
        {element}
      </Tooltip>
    )
    : element;
});

(Checkbox as ComponentType<CheckboxProps>).defaultProps = {};

export default Checkbox;
